/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import useBreakpoints from 'utils/breakpoints';
import useScrollBarWidth from 'utils/scroll-bar-width';
import useKeys from 'utils/keys';
import useMounted from 'utils/mounted';
import Logo from 'components/Icons/Logo/Logo';
import Arrow from 'components/Icons/Arrow/Arrow';
import Globe from 'components/Icons/Globe/Globe';
import Submenu from './Submenu/Submenu';
import './MainNav.scss';

const letters = 'abcdefghijklmnopqrstuvwxyzæøå'.split('');

/**
 * This is the MainNav component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function MainNav({
  logoAnimation,
  search,
  contact,
  home,
  back,
  menu,
  about,
  privacy,
  languages,
  labels,
}) {
  const letterKeys = {};
  const container = useRef(null);
  const menuButton = useRef(null);
  const searchButton = useRef(null);
  const contactButton = useRef(null);
  const submenuNode = useRef(null);
  const mounted = useMounted();
  const breakpoint = useBreakpoints({
    tablet: 830,
    desktop: 1024,
  });
  const scrollBarWidth = useScrollBarWidth();
  const [active, setActive] = useState(false);
  const [open, setOpen] = useState(false);
  const [submenu, setSubmenu] = useState(null);

  function onLetter({ key }) {
    if (open && submenu === 'menu') {
      submenuNode.current.focusOnLetter(key);
    }
  }

  useEffect(() => {
    setActive(submenu !== null);
    if (submenu === null) {
      setOpen(false);
    }
  }, [submenu]);

  useEffect(() => {
    if (!open && mounted) {
      const target = (() => {
        if (submenu === 'contact' && contactButton.current) {
          return contactButton.current;
        }

        if (submenu === 'search' && searchButton.current) {
          return searchButton.current;
        }
        return menuButton.current;
      })();

      if (target) {
        target.focus();
      }
    }
  }, [open, submenu]);

  useEffect(() => {
    if (open && submenu === 'menu') {
      if (submenuNode.current) {
        submenuNode.current.focusOnFirstItem();
      }
    }
  }, [open, submenu]);

  letters.forEach(letter => {
    letterKeys[letter] = onLetter;
  });

  useKeys('mainnav', active, {
    esc: e => {
      if (open) {
        e.preventDefault();
        setOpen(false);
      }
    },

    enter: e => {
      if (!open) {
        e.preventDefault();
        setOpen(true);
        submenuNode.current.focusOnFirstItem();
      }
    },

    space: e => {
      if (!open) {
        e.preventDefault();
        setOpen(true);
        submenuNode.current.focusOnFirstItem();
      }
    },

    down: e => {
      if (submenu === 'menu') {
        setOpen(true);
        e.preventDefault();
        submenuNode.current.focusOnNextItem();
      }
    },

    up: e => {
      if (submenu === 'menu') {
        setOpen(true);
        e.preventDefault();
        submenuNode.current.focusOnPrevItem();
      }
    },

    home: e => {
      if (open && submenu === 'menu') {
        e.preventDefault();
        submenuNode.current.focusOnFirstItem();
      }
    },

    end: e => {
      if (open && submenu === 'menu') {
        e.preventDefault();
        submenuNode.current.focusOnLastItem();
      }
    },

    ...letterKeys,
  });

  useEffect(() => {
    if (open) {
      document.body.classList.add('submenu-open');
      if (scrollBarWidth > 0) {
        document.body.style.paddingRight = `${scrollBarWidth}px`;
        container.current.style.width = `calc(100% - ${scrollBarWidth}px)`;
      } else {
        document.body.style.paddingRight = null;
        container.current.style.width = null;
      }
    } else {
      document.body.classList.remove('submenu-open');
      document.body.style.paddingRight = null;
      container.current.style.width = null;
    }
  }, [open, scrollBarWidth]);

  function onSubmenuFocus(type) {
    if (type) {
      setSubmenu(type);
    } else {
      setSubmenu('menu');
    }
    setOpen(true);
  }

  function onSubmenuBlur() {
    setSubmenu(null);
  }

  function toggleMenu() {
    if (submenu === 'menu') {
      setSubmenu(null);
    } else {
      setSubmenu('menu');
    }
  }

  function renderEnhanceTop() {
    if (breakpoint === null) return null;
    return [
      <button
        ref={searchButton}
        id="searchbutton"
        aria-haspopup="true"
        aria-controls="search"
        aria-expanded={submenu == 'search' && open ? 'true' : 'false'}
        key="search"
        onFocus={() => {
          setSubmenu('search');
        }}
        onClick={() => {
          setOpen(open => !open);
        }}
      >
        {search.title}
      </button>,
      <button
        ref={contactButton}
        id="contactbutton"
        aria-haspopup="true"
        aria-controls="contact"
        aria-expanded={submenu == 'contact' && open ? 'true' : 'false'}
        key="contact"
        onFocus={() => {
          setSubmenu('contact');
        }}
        onClick={() => {
          setOpen(open => !open);
        }}
      >
        {contact.title}
      </button>,
    ];
  }

  function renderLanguages() {
    if (breakpoint === null) return null;
    return (
      <div className="language-picker">
        <Globe aria-hidden="true" />
        {languages.map(({ id, code, short, text, url, active }) => (
          <span key={id} className="language">
            <a
              key={id}
              className={classnames({ active })}
              href={url}
              role="menuitem"
              lang={code}
              title={text}
              tabIndex={open ? null : -1}
            >
              <span>{short}</span>
            </a>
          </span>
        ))}
      </div>
    );
  }

  function renderHome() {
    if (!back)
      return (
        <a className="home" href={home.href} title={home.title}>
          {home.text}
        </a>
      );

    return (
      <>
        <a className="back" href={back.href}>
          <Arrow left title={back.title} />
        </a>
        <a className="home" href={back.href} title={back.title}>
          {home.text}
        </a>
      </>
    );
  }

  return (
    <>
      <nav
        ref={container}
        aria-label={labels.navBar}
        className="page-padding main-nav"
        {...(logoAnimation
          ? {
              event: '',
              'event-name': 'logo-anim-almost-done',
              name: 'fade',
              delay: 800,
              duration: 400,
            }
          : {})}
      >
        <div className="back-home">{renderHome()}</div>
        <a className="logo" href={home.href} title={home.title}>
          <Logo title={home.title} />
        </a>
        <div className="buttons">
          {renderEnhanceTop()}
          <button
            ref={menuButton}
            id="menubutton"
            aria-haspopup="true"
            aria-controls="menu"
            aria-expanded={submenu == 'menu' && open ? 'true' : 'false'}
            onFocus={() => {
              setSubmenu('menu');
            }}
            onClick={() => {
              setSubmenu('menu');
              setOpen(open => !open);
            }}
          >
            {menu.title}
          </button>
          {renderLanguages()}
        </div>
      </nav>
      <div
        className="menu-overlay"
        onClick={() => {
          setOpen(false);
        }}
      />
      <Submenu
        ref={submenuNode}
        breakpoint={breakpoint}
        active={open ? submenu : null}
        home={home}
        menu={menu}
        search={search}
        contact={contact}
        about={about}
        privacy={privacy}
        languages={languages}
        onFocus={onSubmenuFocus}
        onBlur={onSubmenuBlur}
        onSearchClick={() => {
          setSubmenu('search');
          setActive(false);
          setOpen(true);
        }}
        onContactClick={() => {
          setSubmenu('contact');
          setActive(false);
          setOpen(true);
        }}
        onMenuClick={toggleMenu}
        onClose={() => {
          setSubmenu(null);
          setActive(false);
        }}
        labels={labels.submenu}
      />
    </>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
MainNav.propTypes = {
  logoAnimation: PropTypes.bool,
  home: PropTypes.shape({
    href: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,

  back: PropTypes.shape({
    href: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }),

  search: PropTypes.shape({
    title: PropTypes.string.isRequired,
  }).isRequired,

  contact: PropTypes.shape({
    title: PropTypes.string.isRequired,
  }).isRequired,

  about: PropTypes.object.isRequired,

  privacy: PropTypes.object.isRequired,

  menu: PropTypes.shape({
    title: PropTypes.string.isRequired,
  }).isRequired,

  languages: PropTypes.array.isRequired,

  labels: PropTypes.shape({
    navBar: PropTypes.string.isRequired,
    submenu: PropTypes.object.isRequired,
  }).isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
MainNav.defaultProps = {
  logoAnimation: false,
  back: null,
  about: {},
  privacy: {},
  languages: [],
  labels: {
    navBar: 'Main navigation',
    submenu: {
      title: 'Menu',
      menu: {
        languages: 'Languages',
      },
      contact: {
        title: 'Contact',
        address: 'Address',
        phone: 'Phone',
        email: 'Email',
        socials: 'Social medias',
        employees: 'Employees',
      },
    },
  },
};

export default MainNav;
